import { useCallback } from 'react';
import { usePublicConfigurationContext } from 'runtime-configuration';

import { LoggerFactory } from '@videoblocks/typescript-logger';

import { RecaptchaAction } from './RecaptchaAction';

const logger = LoggerFactory.create('useRecaptchaCallback');

/**
 * Returns a callback function that can be used to execute reCAPTCHA v3.
 * Call this function right before submitting a form or making an API request.
 * Be sure to include the token in the request body, and verify it on the server side.
 * see https://developers.google.com/recaptcha/docs/v3
 */
export function useRecaptchaCallback(action: RecaptchaAction): () => Promise<string | undefined> {
    const { recaptchaSiteKey, recaptchaDisabled } = usePublicConfigurationContext();

    return useCallback(async () => {
        if (recaptchaDisabled) {
            logger.warn('skipping recaptcha - recaptcha is disabled');
            return Promise.resolve(undefined);
        }

        if (!grecaptcha) {
            logger.warn('skipping recaptcha - grecaptcha is not defined');
            return Promise.resolve(undefined);
        }

        return new Promise((resolve) => {
            grecaptcha.ready(() => {
                grecaptcha.execute(recaptchaSiteKey, { action }).then((token) => {
                    resolve(token);
                });
            });
        });
    }, [action, recaptchaSiteKey, recaptchaDisabled]);
}
